import { useRef, useState, type ReactNode } from "react";
import { IconArrowDown, IconArrowUp, IconDotsVertical, IconTable } from "@tabler/icons-react";
import {
  Badge,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Loader,
  Skeleton,
  Text,
} from "@/components/ui";
import { extractApiMessage } from "@/shared/api/extract-message";
import type { PreviewInfinito } from "../hooks/use-preview";
import type { RepDefinicion } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";

function celda(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  return String(v);
}

/**
 * Columnas que tendrá la vista previa según la definición · base + calculadas de
 * tipo fila (las agregadas van al pie). Sirve para pintar el skeleton con el
 * número real de columnas mientras la consulta aún no las devuelve.
 */
export function columnasEsperadas(def: RepDefinicion | undefined): number {
  if (!def) return 0;
  const calcFila = (def.columnasCalculadas ?? []).filter((c) => (c.tipo ?? "fila") !== "agregado").length;
  return def.columnas.length + calcFila;
}

const FILAS_SKELETON = 8;

/**
 * Tabla de vista previa con scroll infinito. Recibe el resultado del hook de
 * preview (usuario o admin en contexto de config), así el mismo componente sirve
 * al builder de usuario y al de predefinidos. Si se pasan <c>def</c>+<c>setDef</c>,
 * los encabezados son INTERACTIVOS (ordenar al clic, renombrar al doble clic,
 * quitar) como en el reporteador legado.
 */
export function PreviewTabla({
  activo,
  datos,
  acciones,
  def,
  setDef,
  defEnCarga,
}: {
  activo: boolean;
  datos: PreviewInfinito;
  acciones?: ReactNode;
  def?: RepDefinicion;
  setDef?: (f: (d: RepDefinicion) => RepDefinicion) => void;
  /** Definición (no interactiva) solo para saber cuántas columnas pintar en el skeleton. */
  defEnCarga?: RepDefinicion;
}) {
  const { preview, total, columnas, filas } = datos;
  // Primera carga · aún sin columnas del servidor: se pintan las de la definición.
  const cargandoSinColumnas = preview.isLoading && columnas.length === 0;
  const nColsCarga = columnas.length || columnasEsperadas(def ?? defEnCarga) || 5;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [renombrando, setRenombrando] = useState<number | null>(null);
  const interactivo = !!def && !!setDef;

  // Mapea la columna del preview (por índice) a la columna de la definición: las
  // primeras son las columnas base (def.columnas); las siguientes, las columnas
  // calculadas de tipo fila (las agregadas van al pie, no como columna).
  function refColumna(i: number) {
    if (!def) return null;
    const base = def.columnas.length;
    if (i < base) return { tipo: "base" as const, tablaAlias: def.columnas[i].tablaAlias, token: def.columnas[i].token };
    const filaCalc = (def.columnasCalculadas ?? [])
      .map((c, idx) => ({ c, idx }))
      .filter((x) => (x.c.tipo ?? "fila") !== "agregado");
    const item = filaCalc[i - base];
    return item ? { tipo: "calc" as const, calcIndex: item.idx } : null;
  }

  function ordenar(i: number, dir: "asc" | "desc" | null) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.fijarOrden(d, r.tablaAlias, r.token, dir));
  }
  function quitar(i: number) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.removeColumna(d, i));
    else if (r?.tipo === "calc") setDef!((d) => ops.removeCalc(d, r.calcIndex));
  }
  function renombrar(i: number, nuevo: string) {
    const r = refColumna(i);
    if (r?.tipo === "base") setDef!((d) => ops.updateColumna(d, i, { etiqueta: nuevo }));
    else if (r?.tipo === "calc") setDef!((d) => ops.updateCalc(d, r.calcIndex, { nombre: nuevo }));
    setRenombrando(null);
  }
  function dirOrden(i: number): "asc" | "desc" | null {
    const r = refColumna(i);
    return r?.tipo === "base" && def ? ops.ordenDe(def, r.tablaAlias, r.token) : null;
  }

  function onScroll() {
    const el = scrollRef.current;
    if (!el || !preview.hasNextPage || preview.isFetchingNextPage) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.75) preview.fetchNextPage();
  }

  return (
    <Card className="flex h-full flex-col p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Text fw={600}>Vista previa</Text>
          {activo && (
            <Badge variant="light" size="xs">
              {total.isLoading ? "contando…" : total.data != null ? `${total.data.toLocaleString("es-MX")} filas` : "—"}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">{acciones}</div>
      </div>

      {!activo ? (
        <EmptyState
          icon={IconTable}
          title="Arma tu reporte"
          description="Elige al menos una columna para ver la vista previa."
        />
      ) : preview.isError ? (
        <Text size="sm" c="red" className="p-2">
          {extractApiMessage(preview.error)}
        </Text>
      ) : (
        <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-auto rounded-md border border-border">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr>
                {cargandoSinColumnas &&
                  Array.from({ length: nColsCarga }, (_, i) => (
                    <th key={`sk-${i}`} className="border-b border-border px-3 py-2" aria-hidden="true">
                      <Skeleton height={10} width={i % 2 === 0 ? 90 : 70} />
                    </th>
                  ))}
                {columnas.map((c, i) => {
                  const dir = interactivo ? dirOrden(i) : null;
                  const ref = interactivo ? refColumna(i) : null;
                  const puedeOrdenar = ref?.tipo === "base";
                  return (
                    <th key={c.clave} className="whitespace-nowrap border-b border-border px-3 py-2 text-left font-semibold">
                      {renombrando === i ? (
                        <input
                          autoFocus
                          defaultValue={c.etiqueta}
                          className="h-6 w-full min-w-24 rounded border border-border bg-background px-1 text-sm font-normal"
                          onBlur={(e) => renombrar(i, e.currentTarget.value.trim() || c.etiqueta)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") renombrar(i, e.currentTarget.value.trim() || c.etiqueta);
                            if (e.key === "Escape") setRenombrando(null);
                          }}
                        />
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="truncate">{c.etiqueta}</span>
                          {dir === "asc" && <IconArrowUp size={13} className="shrink-0 text-multiva-600" />}
                          {dir === "desc" && <IconArrowDown size={13} className="shrink-0 text-multiva-600" />}
                          {interactivo && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={`Opciones de la columna ${c.etiqueta}`}
                                  className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground"
                                >
                                  <IconDotsVertical size={14} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                {puedeOrdenar && (
                                  <>
                                    <DropdownMenuItem onClick={() => ordenar(i, "asc")}>
                                      <IconArrowUp size={14} /> Ordenar ascendente
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => ordenar(i, "desc")}>
                                      <IconArrowDown size={14} /> Ordenar descendente
                                    </DropdownMenuItem>
                                    {dir && (
                                      <DropdownMenuItem onClick={() => ordenar(i, null)}>
                                        Quitar orden
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => setRenombrando(i)}>Renombrar…</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => quitar(i)}>Quitar columna</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, i) => (
                <tr key={i} className="odd:bg-background even:bg-muted/40">
                  {columnas.map((c) => (
                    <td key={c.clave} className="whitespace-nowrap border-b border-border/60 px-3 py-1.5">
                      {celda(fila[c.clave])}
                    </td>
                  ))}
                </tr>
              ))}
              {preview.isLoading &&
                Array.from({ length: FILAS_SKELETON }, (_, f) => (
                  <tr key={`sk-${f}`} aria-hidden={f > 0 ? "true" : undefined}>
                    {Array.from({ length: nColsCarga }, (_, i) => (
                      <td key={i} className="border-b border-border/60 px-3 py-2">
                        {f === 0 && i === 0 && (
                          <span role="status" className="sr-only">
                            Cargando vista previa…
                          </span>
                        )}
                        <Skeleton height={12} width={["80%", "60%", "90%", "70%"][(f + i) % 4]} className="min-w-12" />
                      </td>
                    ))}
                  </tr>
                ))}
              {!preview.isLoading && filas.length === 0 && (
                <tr>
                  <td colSpan={Math.max(1, columnas.length)} className="px-3 py-6 text-center text-muted-foreground">
                    Sin resultados para esta definición.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {preview.isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Loader size="xs" />
            </div>
          )}
        </div>
      )}

      {/* Pie de totales · columnas calculadas de tipo agregado (RN-18) */}
      {activo && datos.totales && Object.keys(datos.totales).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 border-t border-border pt-2 text-sm">
          {Object.entries(datos.totales).map(([k, v]) => (
            <span key={k}>
              <span className="text-muted-foreground">{k}:</span>{" "}
              <span className="font-semibold tabular-nums">{celda(v)}</span>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}
